import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrial } from '../context/TrialContext';
import { supabase } from '../supabase';
import { fetchPlanByKey, createPaymentLink, formatPrice } from '../utils/astropayService';
import toast from 'react-hot-toast';
import {
  Lock,
  CreditCard,
  Clock,
  CheckCircle,
  Building2,
  ArrowRight,
  LogOut,
  Timer,
  ExternalLink,
  Zap,
  Shield,
  Star,
  Crown,
  AlertTriangle,
} from 'lucide-react';
import { desactivarAvisosAdminEsteDispositivo } from '../utils/pushSubscription';

export default function PagoRequerido() {
  const navigate = useNavigate();
  const {
    daysLeft,
    isBlocked,
    isPending,
    isTrialing,
    isActive,
    isGrace,
    isExpired,
    isReadOnly,
    graceDaysLeft,
    currentPlan,
    currentPlanKey,
    paymentUrl,
    latestSubscription,
    organization,
    loadingTrial,
    refreshTrial,
    billingProviderLabel,
  } = useTrial();

  const [planDetails, setPlanDetails] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Fetch plan details if not from context
  useEffect(() => {
    if (currentPlan) {
      setPlanDetails(currentPlan);
    } else if (currentPlanKey) {
      fetchPlanByKey(currentPlanKey).then((p) => {
        if (p) setPlanDetails(p);
      });
    }
  }, [currentPlan, currentPlanKey]);

  const handleLogout = async () => {
    // Avisos push: este equipo deja de recibir los pedidos de la empresa.
    await desactivarAvisosAdminEsteDispositivo();
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleGeneratePayment = async () => {
    if (!organization?.id || !planDetails) return;
    setGenerating(true);
    try {
      const result = await createPaymentLink({
        orgId: organization.id,
        planKey: planDetails.key || currentPlanKey,
        planName: planDetails.name,
        amount: planDetails.price_monthly,
        currency: planDetails.currency || 'ARS',
      });

      await refreshTrial();

      if (result.payment_url) {
        toast.success('Link de pago generado');
        window.open(result.payment_url, '_blank');
      } else {
        toast.success('Suscripción registrada. Subí tu comprobante para activar.', { duration: 5000 });
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error al generar link de pago');
    } finally {
      setGenerating(false);
    }
  };

  if (loadingTrial) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center p-4">
        <div className="w-10 h-10 border-2 border-[#E4E4E7] border-t-[#10B981] rounded-full animate-spin" />
      </div>
    );
  }

  const statusConfig = isActive
    ? {
        icon: <CheckCircle size={32} className="text-[var(--color-accent-dark)]" />,
        title: 'Suscripción vigente',
        subtitle: 'Tu cuenta ya está activa.',
        desc: 'No necesitás subir un comprobante ahora porque el período actual sigue vigente.',
        bg: 'bg-[var(--color-accent-soft)]',
        border: 'border-[color:var(--color-accent)]',
      }
    : isPending
    ? {
        icon: <Clock size={32} className="text-amber-500" />,
        title: 'Pago en proceso',
        subtitle: `Estamos esperando la confirmación de ${billingProviderLabel}.`,
        desc: 'Apenas el pago se acredite, tu cuenta se activa automáticamente. Suele tardar minutos.',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
      }
    : isGrace
    ? {
        icon: <AlertTriangle size={32} className="text-orange-500" />,
        title: 'Período de gracia',
        subtitle: `Te quedan ${graceDaysLeft} día${graceDaysLeft === 1 ? '' : 's'} antes del modo solo lectura.`,
        desc: 'Tu período de prueba terminó. Elegí un plan para seguir usando Gastify sin restricciones.',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
      }
    : isExpired || isReadOnly
    ? {
        icon: <Lock size={32} className="text-red-500" />,
        title: 'Suscripción vencida',
        subtitle: 'Tu cuenta está en modo solo lectura.',
        desc: 'Podés ver toda tu información, pero no podés vender, facturar ni importar datos. Abonalo para reactivar.',
        bg: 'bg-red-50',
        border: 'border-red-200',
      }
    : isBlocked
    ? {
        icon: <Lock size={32} className="text-red-500" />,
        title: 'Cuenta bloqueada',
        subtitle: 'Tu cuenta fue bloqueada por el administrador.',
        desc: 'Contactá al soporte de Gastify para reactivarla.',
        bg: 'bg-red-50',
        border: 'border-red-200',
      }
    : {
        icon: <Timer size={32} className="text-purple-500" />,
        title: `${daysLeft} días de prueba restantes`,
        subtitle: 'Tu período actual sigue vigente.',
        desc: 'Mientras la prueba siga activa, no hace falta pagar. Podés elegir tu plan en cualquier momento.',
        bg: 'bg-purple-50',
        border: 'border-[#D1FAE5]',
      };

  const showPaymentSection = isBlocked || isGrace || isExpired || isReadOnly;

  const planIcon =
    currentPlanKey === 'enterprise' ? (
      <Crown size={18} className="text-[var(--color-accent-dark)]" />
    ) : currentPlanKey === 'premium' ? (
      <Star size={18} className="text-purple-500" />
    ) : (
      <Shield size={18} className="text-blue-500" />
    );

  return (
    <div className="min-h-screen bg-[#FBF8F1] flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="mb-8 flex flex-col items-center">
        <div className="h-14 w-14 rounded-md bg-[#10B981] flex items-center justify-center shadow-lg mb-3">
          <span className="text-white font-black text-[24px]">G</span>
        </div>
        <span className="text-[18px] font-black text-[#3F3F46]">Gastify</span>
        <span className="text-[11px] text-[#A1A1AA] font-semibold uppercase tracking-widest">
          Gestioná sin complicarte
        </span>
      </div>

      {/* Status card */}
      <div
        className={`w-full max-w-md rounded-md border ${statusConfig.border} ${statusConfig.bg} p-6 mb-6 flex gap-4`}
      >
        <div className="shrink-0 mt-0.5">{statusConfig.icon}</div>
        <div>
          <h2 className="text-[16px] font-black text-[#3F3F46] mb-1">{statusConfig.title}</h2>
          <p className="text-[13px] font-semibold text-[#3F3F46] mb-1">{statusConfig.subtitle}</p>
          <p className="text-[12px] text-[#A1A1AA]">{statusConfig.desc}</p>
        </div>
      </div>

      {/* Plan & pricing (dynamic from DB) */}
      {showPaymentSection && planDetails && (
        <div className="w-full max-w-md bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-6 mb-4 shadow-sm">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-[#A1A1AA] mb-4">
            Tu plan de suscripción
          </h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-[14px] text-[#3F3F46] font-bold flex items-center gap-2">
                {planIcon}
                {planDetails.name}
              </span>
              <span className="text-[16px] font-black text-[#3F3F46]">
                {formatPrice(planDetails.price_monthly)}
                <span className="text-[11px] font-bold text-[#A1A1AA]"> /mes</span>
              </span>
            </div>

            <p className="text-[12px] text-[#71717A]">{planDetails.description}</p>

            {Array.isArray(planDetails.features) && planDetails.features.length > 0 && (
              <div className="space-y-1.5 pt-2">
                {planDetails.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px] text-[#3F3F46]">
                    <CheckCircle size={13} className="text-[#10B981] shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-[#F4F4F5]">
            <button
              onClick={() => navigate('/sistema/gestion-plan')}
              className="text-[11px] font-bold text-[#10B981] hover:underline flex items-center gap-1"
            >
              Cambiar de plan <ArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Payment options — solo MP / AstroPay (sin transferencias) */}
      {showPaymentSection && (
        <div className="w-full max-w-md space-y-3 mb-4">
          {paymentUrl ? (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-[#10B981] text-white font-black text-[15px] hover:bg-[#059669] transition-all shadow-md hover:shadow-lg"
            >
              <CreditCard size={18} />
              {`Pagar mi plan con ${billingProviderLabel}`}
              <ExternalLink size={14} />
            </a>
          ) : (
            <button
              onClick={handleGeneratePayment}
              disabled={generating || !planDetails}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-[#10B981] text-white font-black text-[15px] hover:bg-[#059669] transition-all shadow-md hover:shadow-lg disabled:opacity-50"
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  {`Pagar mi plan con ${billingProviderLabel}`}
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          )}

          {/* Mini-trust strip: dejá claro que el cobro es por MP, sin tarjeta guardada en Gastify. */}
          <p className="text-center text-[11px] text-[#A1A1AA] leading-relaxed px-2">
            Pago seguro a través de {billingProviderLabel}. No guardamos tus datos de tarjeta.
          </p>
        </div>
      )}

      {/* Back to system */}
      <div className="w-full max-w-md space-y-3">
        {isPending && (
          <div className="w-full flex items-start gap-3 py-3 px-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-[12px] font-medium">
            <Clock size={16} className="shrink-0 mt-0.5 text-amber-500" />
            <span>
              Estamos confirmando tu pago con {billingProviderLabel}. Apenas se acredite, tu cuenta se activa sola. No hace falta que hagas nada.
            </span>
          </div>
        )}

        {(isTrialing || isPending || isActive || isGrace || isExpired) && (
          <button
            onClick={() => navigate('/sistema')}
            className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-[var(--surface)] border border-[#E4E4E7] text-[#3F3F46] font-bold text-[13px] hover:bg-[#F4F4F5] transition-all"
          >
            {isExpired ? 'Entrar al sistema (solo lectura)' : 'Volver al sistema'}
          </button>
        )}

        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-6 rounded-xl text-[#A1A1AA] font-semibold text-[12px] hover:text-[#3F3F46] transition-all"
        >
          <LogOut size={14} />
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
